/* eslint-disable no-param-reassign */
import { createSlice } from "@reduxjs/toolkit";
import { DEFAULT_CONFIGURATION, STEPS } from "../../../Common/store/data/commonData";
import {
  DEFAULT_AXIS_OPTIONS,
  DEFAULT_AXIS_SELECTION,
  DEFAULT_CARTESIAN_FILTERS,
  DEFAULT_DYNAMICS,
  DEFAULT_KINEMATICS,
  DEFAULT_MECHANICS
} from "../data/cartesianData";

import {
  setCurrentStepImplementation,
  setSavedConfigurationsImplementation
} from "../../../Common/store/implementations/commonImplementation";
import {
  resetDynamicsImplementation,
  resetMechanicsImplementation,
  setDynamicsCycleRunningImplementation,
  setDynamicsImplementation,
  setDynamicsStepsImplementation,
  setKinematicsImplementation,
  setMechanicsImplementation
} from "../implementations/cartesianImplementation";

const initialState = {
  configuration: null,
  defaultConfiguration: {
    ...DEFAULT_CONFIGURATION,
    technology: "Cartesian",
    configurationType: "CARTESIAN",
    kinematics: DEFAULT_KINEMATICS,
    mechanics: DEFAULT_MECHANICS,
    dynamics: DEFAULT_DYNAMICS,
    axisSelection: DEFAULT_AXIS_SELECTION,
    axisOptions: DEFAULT_AXIS_OPTIONS,
    productCode: "",
    cadenasProductCode: ""
  },
  steps: STEPS.CARTESIAN,
  currentStep: STEPS.CARTESIAN.START,
  axesToCompare: [],
  selectedBundle: null,
  readOnlyMode: false,
  showReadOnlyDialogPopup: false,
  savedDependenciesPayloads: null,
  possibleFiltersValues: null,
  defaultFilters: DEFAULT_CARTESIAN_FILTERS,
  savedConfigurations: []
};

export const cartesian = createSlice({
  name: "cartesian",
  initialState,
  reducers: {
    setMechanicsHash: (state, action) => {
      const { configuration } = state;
      configuration.configData.mechanics.hash = action.payload;
    },
    setDynamicsHash: (state, action) => {
      const { configuration } = state;
      configuration.configData.dynamics.hash = action.payload;
    },
    setDynamicsMovementValid: (state, action) => {
      const { configuration } = state;
      configuration.configData.dynamics.movementValid = action.payload;
    },
    setDynamicsStrokeLength: (state, action) => {
      const { configuration } = state;
      configuration.configData.dynamics.strokeLength = action.payload;
    },
    setSelectedMechanicsField: (state, action) => {
      const { configuration } = state;
      configuration.configData.mechanics.selectedField = action.payload;
    },
    setSelectedMechanicsFieldIndex: (state, action) => {
      const { configuration } = state;
      configuration.configData.mechanics.selectedIndex = action.payload;
    },
    setKinematics: (state, action) => {
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        state.showReadOnlyDialogPopup = true;
        return;
      }
      setKinematicsImplementation(state, action);
    },
    setMechanics: (state, action) => {
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        state.showReadOnlyDialogPopup = true;
        return;
      }
      setMechanicsImplementation(state, action);
    },
    resetMechanics: (state) => {
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        state.showReadOnlyDialogPopup = true;
        return;
      }
      resetMechanicsImplementation(state);
    },
    setDynamics: (state, action) => {
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        state.showReadOnlyDialogPopup = true;
        return;
      }
      setDynamicsImplementation(state, action);
    },
    resetDynamics: (state) => {
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        state.showReadOnlyDialogPopup = true;
        return;
      }
      resetDynamicsImplementation(state);
    },
    setDynamicsSteps: (state, action) => {
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        state.showReadOnlyDialogPopup = true;
        return;
      }
      setDynamicsStepsImplementation(state, action);
    },
    setDynamicsCycleRunning: (state, action) => {
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        state.showReadOnlyDialogPopup = true;
        return;
      }
      setDynamicsCycleRunningImplementation(state, action);
    },
    validateDynamics: (state, action) => {
      const { configuration } = state;
      // eslint-disable-next-line no-param-reassign
      configuration.configData.dynamics.isValid = action.payload;
    },
    setAxisSelection: (state, action) => {
      /* READ ONLY VERIFICATION */
      const { readOnlyMode } = state;
      const { configuration } = state;
      const existingId = configuration?.configData?.axisSelection?.selectedAxis?.id;
      const newAxisId = action.payload.selectedAxis?.id;
      const isSameAxis = existingId === newAxisId;
      if (readOnlyMode) {
        if (!isSameAxis) {
          state.showReadOnlyDialogPopup = true;
        }
        return;
      }
      configuration.configData.axisSelection = action.payload;
    },
    setAxesCombinations: (state, action) => {
      const { configuration } = state;
      // eslint-disable-next-line no-param-reassign
      configuration.configData.axisSelection.axesCombinations = action.payload;
    },
    setAxisOptions: (state, action) => {
      /* READ ONLY VERIFICATION */
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        // eslint-disable-next-line no-param-reassign
        state.showReadOnlyDialogPopup = true;
        return;
      }

      const { configuration } = state;

      // eslint-disable-next-line no-param-reassign
      configuration.configData.axisOptions = action.payload;
    },
    setCartesianConfiguration: (state, action) => {
      state.configuration = action.payload;

      // set currentStepId
      const stepsAsArray = Object.values(state.steps);
      // eslint-disable-next-line no-param-reassign
      state.currentStep = stepsAsArray.find((step) => step.id === action.payload.configData.currentStepId);
    },
    setCartesianReadOnlyMode: (state, action) => {
      state.readOnlyMode = action.payload;
    },
    setCartesianShowReadOnlyDialogPopup: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.showReadOnlyDialogPopup = action.payload;
    },
    setCartesianCurrentStep: (state, action) => {
      setCurrentStepImplementation(state, action);
    },
    resetCartesian: (state) => {
      const resetState = { ...initialState };
      resetState.possibleFiltersValues = state.possibleFiltersValues;
      return resetState;
    },
    setCartesianConfigData: (state, action) => {
      /* READ ONLY VERIFICATION */
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        // eslint-disable-next-line no-param-reassign
        state.showReadOnlyDialogPopup = true;
        return;
      }

      const { configuration } = state;

      // eslint-disable-next-line no-param-reassign
      configuration.configData = action.payload;
    },
    addAxisToCompare: (state, action) => {
      const { axis } = action.payload;
      var axesToCompareTmp = [...state.axesToCompare];
      // if the axis is in the list, remove it
      // else add it (maximum 3)
      const findAxisIndex = axesToCompareTmp.findIndex((a) => a.id === axis.id);
      if (findAxisIndex !== -1) {
        axesToCompareTmp = axesToCompareTmp.filter((a) => a.id !== axis.id);
      } else if (axesToCompareTmp.length < 3) {
        axesToCompareTmp.push(axis);
      }
      state.axesToCompare = axesToCompareTmp;
    },
    resetAxesToCompare: (state) => {
      state.axesToCompare = [];
    },
    setAxesToCompare: (state, action) => {
      state.axesToCompare = action.payload;
    },
    setSelectedAxis: (state, action) => {
      // we have only 1 selected axis
      // we get the axis from the action.payload
      // const axis = action.payload;
      // we put it in axisSelection
      // eslint-disable-next-line no-param-reassign
      /* const { readOnlyMode } = state;
      const { configuration } = state;
      const existingId = configuration?.configData?.axisSelection?.selectedAxis?.id;
      const newAxisId = action.payload?.selectedAxis?.id;
      const isSameAxis = existingId === newAxisId;
      if (readOnlyMode) {
        if (!isSameAxis) {
          state.showReadOnlyDialogPopup = true;
        }
        return;
      } */
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        state.showReadOnlyDialogPopup = true;
        return;
      }
      state.configuration.configData.axisSelection.selectedAxis = action.payload;
    },
    setSelectedBundle: (state, action) => {
      const bundle = action.payload;
      const { readOnlyMode } = state;
      console.log("setSelectedBundle", bundle);

      if (readOnlyMode) {
        state.showReadOnlyDialogPopup = true;
        return;
      }

      if (!bundle) {
        state.configuration.configData.axisOptions.selectedBundle = null;
        return;
      }

      const newBundle = {
        motorReference: bundle.motor.commercialReference,
        driveReference: bundle.drive.commercialReference,
        gearboxReference: bundle.gearbox ? bundle.gearbox.commercialReference : null,
        adaptationKitReference: bundle.adaptationKit ? bundle.adaptationKit.commercialReference : null,
        bundleData: bundle
      };

      // eslint-disable-next-line no-param-reassign
      state.configuration.configData.axisOptions.selectedBundle = newBundle;
    },
    //implement the setSelectedCartesianAdaptationKit action
    setSelectedCartesianAdaptationKit: (state, action) => {
      state.configuration.configData.axisOptions.cartesianAdaptationKit = action.payload;
    },

    setBundleChanged: (state, action) => {
      state.configuration.configData.axisOptions.bundleChanged = action.payload;
    },
    setAxisOptionsIsValid: (state, action) => {
      state.configuration.configData.axisOptions.isValid = action.payload;
    },
    setCartesianPossibleFiltersValues: (state, action) => {
      const possibleFiltersValuesFromS3 = action.payload;

      state.possibleFiltersValues = possibleFiltersValuesFromS3;
    },
    setCartesianFilters: (state, action) => {
      const filters = action.payload;

      state.configuration.configData.axisSelection.selectedFilters = filters;
    },
    setCartesianSavedConfigurations: (state, action) => {
      setSavedConfigurationsImplementation(state, action);
    },
    setCartesianProductCode: (state, action) => {
      const { configuration } = state;
      const productCode = action.payload;

      configuration.configData.productCode = productCode;
    },
    setCartesianCadenasProductCode: (state, action) => {
      const { configuration } = state;
      const cadenasProductCode = action.payload;

      configuration.configData.cadenasProductCode = cadenasProductCode;
    },
    setCartesianDriveQuantity: (state, action) => {
      const { configuration } = state;
      const driveQuantity = action.payload;

      configuration.configData.driveQuantity = driveQuantity;
    },
    setCartesianAdaptationKitWithInBundleQuantity: (state, action) => {
      const { configuration } = state;
      const adaptationKitWithinBundleQty = action.payload;

      configuration.configData.adaptationKitWithinBundleQty = adaptationKitWithinBundleQty;
    },
    setCartesianAdaptationKitToBundleQuantity: (state, action) => {
      const { configuration } = state;
      const adaptationKitToBundleQty = action.payload;

      configuration.configData.adaptationKitToBundleQty = adaptationKitToBundleQty;
    },
    setCartesianAxisQuantity: (state, action) => {
      const { configuration } = state;
      const axisQty = action.payload;

      configuration.configData.axisQty = axisQty;
    }
  }
});

export const {
  setMechanicsHash,
  setDynamicsHash,
  setDynamicsMovementValid,
  setDynamicsStrokeLength,
  setSelectedMechanicsField,
  setSelectedMechanicsFieldIndex,
  setKinematics,
  setMechanics,
  resetMechanics,
  setDynamics,
  resetDynamics,
  setDynamicsSteps,
  setDynamicsCycleRunning,
  validateDynamics,
  setAxisSelection,
  setAxesCombinations,
  setAxisOptions,
  setCartesianConfiguration,
  resetCartesian,
  setCartesianCurrentStep,
  setCartesianReadOnlyMode,
  setCartesianShowReadOnlyDialogPopup,
  setCartesianConfigData,
  addAxisToCompare,
  resetAxesToCompare,
  setAxesToCompare,
  setSelectedAxis,
  setSelectedBundle,
  setSelectedCartesianAdaptationKit,
  setBundleChanged,
  setAxisOptionsIsValid,
  setCartesianPossibleFiltersValues,
  setCartesianFilters,
  setCartesianSavedConfigurations,
  setCartesianProductCode,
  setCartesianCadenasProductCode,
  setCartesianDriveQuantity,
  setCartesianAdaptationKitWithInBundleQuantity,
  setCartesianAdaptationKitToBundleQuantity,
  setCartesianAxisQuantity
} = cartesian.actions;

export const selectDefaultCartesianConfiguration = (state) => state.cartesian.defaultConfiguration;
export const selectKinematics = (state) => state.cartesian.configuration?.configData?.kinematics;
export const selectMechanics = (state) => state.cartesian.configuration?.configData?.mechanics;
export const selectDynamics = (state) => state.cartesian.configuration?.configData?.dynamics;
export const selectCartesianConfiguration = (state) => state.cartesian.configuration;
export const selectCartesianSteps = (state) => state.cartesian.steps;
export const selectCartesianCurrentStep = (state) => state.cartesian.currentStep;
export const selectCartesianReadOnlyMode = (state) => state.cartesian.readOnlyMode;
export const selectCartesianShowReadOnlyDialogPopup = (state) => state.cartesian.showReadOnlyDialogPopup;
export const selectAxisSelection = (state) => state.cartesian.configuration?.configData?.axisSelection;
export const selectAxisOptions = (state) => state.cartesian.configuration?.configData?.axisOptions;
export const selectAxesToCompare = (state) => state.cartesian.axesToCompare;
export const selectSelectedAxis = (state) => state.cartesian.configuration?.configData?.axisSelection?.selectedAxis;
export const selectAxesCombinations = (state) =>
  state.cartesian.configuration?.configData?.axisSelection?.axesCombinations;
export const selectSelectedBundle = (state) => state.cartesian.configuration?.configData?.axisOptions?.selectedBundle;
export const selectCartesianAdaptationKit = (state) =>
  state.cartesian.configuration?.configData?.axisOptions?.cartesianAdaptationKit;
export const selectCartesianPossibleFiltersValues = (state) => state.cartesian.possibleFiltersValues;
export const selectDefaultFilters = (state) => state.cartesian.defaultFilters;
export const selectCartesianFilters = (state) =>
  state.cartesian.configuration?.configData?.axisSelection?.selectedFilters;
export const selectCartesianSavedConfigurations = (state) => state.cartesian.savedConfigurations;
export const selectDriveQuantity = (state) => state.cartesian.configuration?.configData?.driveQuantity;
export const selectAdaptationKitWithinBundleQty = (state) =>
  state.cartesian.configuration?.configData?.adaptationKitWithinBundleQty;
export const selectAdaptationKitToBundleQty = (state) =>
  state.cartesian.configuration?.configData?.adaptationKitToBundleQty;
export const selectAxisQty = (state) => state.cartesian.configuration?.configData?.axisQty;
export const selectBillOfMaterials = (state) => state.cartesian.configuration?.configData?.billOfMaterials;

export default cartesian.reducer;
